/* eslint-disable max-lines */
import { UserBadgePrintStats } from '../shared/cashier';
import { Config, MenuLink } from '../shared/config';
import {
  ConventionListItem,
  ConventionRedactOptions,
  ConventionSettingsInfo,
  ConventionSharedModel,
} from '../shared/convention';
import {
  DealerArea,
  DealerAssistanceModel,
  DealerConfig,
  DealerForm,
  DealerFullModel,
  DealerModel,
  SeatingPreference,
  UpdateDealerApplicationPayload,
} from '../shared/dealer';
import { Device } from '../shared/device';
import {
  EmailPreview,
  EmailTargets,
  EmailTemplateInfo,
  EmailTemplateList,
  EmailTemplatePreview,
  MailQueueInfo,
  SendEmailPayload,
  Senders,
} from '../shared/email';
import { AuthError } from '../shared/error';
import { ExportListItem, ExportRequest } from '../shared/export';
import {
  ExtendedHotelReleaseGroup,
  FullHotel,
  HotelIntegration,
  HotelPublicInformation,
  HotelReleaseGroup,
  HotelReleaseGroupCreateAttributes,
  HotelReleaseGroupEntries,
  HotelReleaseGroupEntryList,
  HotelReleaseGroupUpdateAttributes,
  HotelReservation,
  HotelStats,
  PublicHotelReleaseGroup,
  UserHotelSummary,
} from '../shared/hotel';
import { FormPageModel } from '../shared/kiosk/models';
import { NewSecretModel, OAuthApplicationModel } from '../shared/oauth';
import {
  Option,
  OptionDataPayload,
  OptionInternal,
  OptionSavePayload,
  OptionSource,
} from '../shared/options';
import {
  CreateProductDiscountPayload,
  OrderSearch,
  RefundResponse,
  UpdateProductDiscountPayload,
} from '../shared/orders';
import { FeeModel } from '../shared/orders/fee';
import { Order, PaymentGateway } from '../shared/orders/model';
import { OrderPaymentStatus, PrepareChargeResult } from '../shared/orders/payment';
import { ProductCategoryModel, ProductEligibility, ProductModel } from '../shared/orders/product';
import { Raffle, RaffleTicket, RaffleWithUserDetails } from '../shared/orders/raffle';
import {
  CreateVoucherModel,
  UpdateVoucherModel,
  VoucherListItem,
  VoucherWithDetails,
} from '../shared/orders/voucher';
import { PaymentGatewayConfiguration } from '../shared/payment_gateway/config';
import {
  GetOrderPagSeguroCheckoutsResponse,
  PagSeguroConfiguration,
} from '../shared/payment_gateway/pagseguro';
import { StripeConnectLinkResponse } from '../shared/payment_gateway/stripeconnect';
import { PermissionName } from '../shared/permissions';
import { Policy, PolicyCreate, PolicyDelete } from '../shared/policies';
import {
  ConRegistrationForm,
  RegistrationFlagModel,
  RegistrationFlagUser,
  RegistrationInfo,
  RegistrationUpsert,
} from '../shared/registration/model';
import { SettingTermKeys } from '../shared/settings';
import {
  AttendeeStats,
  CashierStats,
  CashierStatsByDay,
  DealerStats,
  IdentityStats,
  OptionsStats,
  OptionStatsData,
  RegistrationStats,
  RegsOverYear,
  SalesReport,
} from '../shared/stats';
import { BadgeData, BadgeDataRequest, BadgeDesign, DesignerNode } from '../shared/user/badgeDesign';
import { CurrentUser, UserAllRegistrations } from '../shared/user/base';
import { CashierCheck, NFCBadge, NFCPassword } from '../shared/user/cashier';
import {
  CreateSubUser,
  CreateSubUserResponse,
  CreateUser,
  CreateUserResponse,
} from '../shared/user/create';
import { ExtendedUser } from '../shared/user/extended';
import { NewIdentitySession, StripeIdentityUser } from '../shared/user/identity';
import { UserNoteCreate, UserNoteSearch, UserNoteSummary } from '../shared/user/notes';
import {
  MinimalSecondFactor,
  PrepareBackupResult,
  PrepareOTPResult,
  SecondFactorType,
} from '../shared/user/password';
import {
  CreateRoleInvitesPayload,
  RoleInvite,
  RoleInviteResponse,
  RoleUserListItem,
  RoleWithPermissions,
  UpdateRoleInvitesPayload,
} from '../shared/user/roles';
import { UserSearchFilterContext, UserSearchResult } from '../shared/user/search';
import { UpdateUserPayload } from '../shared/user/update';
import {
  DepartmentWithLeads,
  VolunteerForm,
  VolunteerLoadModel,
  VolunteerModel,
  VolunteerUpsertModel,
} from '../shared/volunteer';
import { VolunteerConfig } from '../shared/volunteer/config';
import { RemoveStaffRequest, StaffRegistrationInfo } from '../shared/volunteer/staff';
import { VolunteerStats } from '../shared/volunteer/statst';
import {
  BadgeArt,
  ChildRegistration,
  LogResult,
  Permission,
  Role,
  SocialLink,
  SocialLinkEndpoint,
  Surcharge,
  SurchargeOrderItem,
  SurchargeProduct,
  UserLocation,
  UserPermissionVerbose,
} from './models';
import { Ticket } from './models/ticket';
import { isAuthError, isResourceError } from './utils';
import {
  APIClient as BaseAPIClient,
  ListResponse,
  QueryParameters,
  RequestOptions,
} from './utils/APIClient';
import { deepMerge } from './utils/deepMerge';

export interface PaymentGatewayConfig {
  type: 'stripe' | 'stripeConnect';
  publishableKey: string;
  accountId?: string;
}

export interface Terms {
  text: string;
}

export type UniqueAttribute = 'email' | 'username';

export interface AttendeeEmails {
  productIds: number[];
  regStatus: { paid: boolean; unpaid: boolean };
  withChildren: boolean;
}

interface OAuthParams {
  access_token: string;
  client_id: string;
}

export interface DataPoint {
  count: number;
  time: number | string;
}

interface RegsOverTime {
  perDay: DataPoint[];
  perHour: DataPoint[];
  perQuarterHour: DataPoint[];
}

export interface ScopeInfo {
  header: string;
  icon: string;
  description: string;
  name: string;
}

export interface OAuthApp {
  clientId: number;
  description?: string;
  name: string;
  scopeInfo?: ScopeInfo[];
  userAlreadyAuthorized: boolean;
}

export interface OAuthAuthorization {
  redirectUri: string;
  token: string;
}

interface EmailQueueStatus {
  remaining: number;
}

interface EmailEstimate {
  count: number;
  userIds: number[];
}

interface ProductCartDeleteParams {
  orderItemId?: number;
  productId?: number;
  referenceId?: number;
}

interface AssetCreateResult {
  assetId: string;
}

export interface Webhook {
  id: number;
  name: string;
  url: string[];
  events: string[];
}

export type RoleScope = 'convention' | 'global';

export class APIClient {
  public constructor(private readonly base: BaseAPIClient) {}

  public async getSecondFactors(userId: number): Promise<MinimalSecondFactor[]> {
    return await this.request<MinimalSecondFactor[]>(`/api/users/${userId}/2fa`);
  }

  public async deleteSecondFactor(factorId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/2fa/${factorId}`,
    });
  }

  public async prepareSecondFactor(type: SecondFactorType.Otp): Promise<PrepareOTPResult>;
  public async prepareSecondFactor(type: SecondFactorType.Backup): Promise<PrepareBackupResult>;
  public async prepareSecondFactor(type: SecondFactorType): Promise<object> {
    return await this.request<object>({
      method: 'post',
      payload: {
        type,
      },
      url: '/api/2fa',
    });
  }

  public async getWebhooks(): Promise<Webhook[]> {
    return await this.request<Webhook[]>('/api/webhooks');
  }

  public async getWebhookTypes(): Promise<string[]> {
    return await this.request<string[]>('/api/webhooks/types');
  }

  public async getWebhookKey(): Promise<object> {
    return await this.request<object>('/api/webhooks/key');
  }

  public async deleteWebhook(id: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/webhooks/${id}`,
    });
  }

  public async testWebhook(id: number): Promise<void> {
    await this.request({
      method: 'post',
      url: `/api/webhooks/${id}/test`,
    });
  }

  public async login(usernameOrMail: string, password: string): Promise<void> {
    await this.request({
      method: 'post',
      payload: {
        password,
        usernameOrMail,
      },
      url: '/api/login',
    });
  }

  public async logout(
    oauth?: OAuthParams | { redirect_token: string },
  ): Promise<{ redirect_token: string } | undefined> {
    return await this.request<{ redirect_token: string } | undefined>({
      url: '/api/users/logout',
      query: oauth as QueryParameters,
    });
  }

  public async getActiveUser(): Promise<CurrentUser> {
    return await this.request<CurrentUser>('/api/users/current');
  }

  public async updateUser(
    userId: number,
    user: UpdateUserPayload,
  ): Promise<{ profilePictureUrl: string }> {
    return await this.request({
      url: `/api/users/${userId}`,
      payload: user,
      method: 'patch',
    });
  }

  public async deleteUser(userId: number): Promise<void> {
    await this.request({
      url: `/api/users/${userId}`,
      method: 'delete',
    });
  }

  public async createUser(user: CreateUser): Promise<CreateUserResponse> {
    return await this.request<CreateUserResponse>({
      method: 'post',
      payload: user,
      url: '/api/users',
    });
  }

  public async requestDeleteUser(userId: number): Promise<void> {
    await this.request({
      url: `/api/users/${userId}/delete`,
      method: 'post',
      payload: {},
    });
  }

  public async cancelUserDeleteRequest(userId: number): Promise<void> {
    await this.request({
      url: `/api/users/${userId}/delete`,
      method: 'delete',
    });
  }

  public async searchUsers(
    search: string,
    filterContext?: UserSearchFilterContext,
  ): Promise<UserSearchResult> {
    const query: QueryParameters = { search };
    if (filterContext) {
      query['printedUsers'] = filterContext.printedUsers;
      query['paidUsers'] = filterContext.paidUsers;
      query['volunteers'] = filterContext.volunteers;
      query['dealers'] = filterContext.dealers;
      query['badgeName'] = filterContext.badgeName;
      query['page'] = filterContext.page;
      query['pageSize'] = filterContext.pageSize;
    }

    return await this.request<UserSearchResult>({
      query,
      url: '/api/users',
    });
  }

  public async getUsersByIds(ids: number[]): Promise<UserSearchResult> {
    if (ids.length === 0) {
      return { total: 0, users: [] };
    }

    return await this.request<UserSearchResult>({
      query: {
        ids: ids.join(','),
      },
      url: '/api/users',
    });
  }

  public async verifyUserIdentity(id: number, verify: boolean): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { verify },
      url: `/api/users/${id}/verify`,
    });
  }

  public async upsertRegistration(
    userId: number,
    payload: Partial<RegistrationUpsert>,
  ): Promise<void> {
    await this.request({
      method: 'post',
      payload,
      url: `/api/users/${userId}/registration`,
    });
  }

  public async createSubUser(payload: CreateSubUser): Promise<CreateSubUserResponse> {
    return await this.request<CreateSubUserResponse>({
      method: 'post',
      payload,
      url: `/api/subaccounts`,
    });
  }

  public async setActiveRegistrationPayment(orderItemId: number): Promise<void> {
    await this.request({
      method: 'patch',
      payload: {},
      url: `/api/orders/items/${orderItemId}/activate`,
    });
  }

  public async setOrderItemsAsFulfilled(userId: number, orderItemIds: number[]): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { orderItemIds },
      url: `/api/users/${userId}/fulfill`,
    });
  }

  public async undoOrderItemDelivery(orderId: number, orderItemId: number): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { orderItemId },
      url: `/api/orders/${orderId}/undo`,
    });
  }

  public async deleteRegistrationPayment(userId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/users/${userId}/registration/payment`,
    });
  }

  public async retrieveStripePaymentInfo(): Promise<PrepareChargeResult> {
    return await this.request<PrepareChargeResult>('/api/pay/gateways/external');
  }

  public async getOrderStatus(orderId: number): Promise<OrderPaymentStatus> {
    return await this.request<OrderPaymentStatus>(`/api/orders/${orderId}/status`);
  }

  public async settleOrder(orderId: number): Promise<void> {
    await this.request({
      method: 'post',
      payload: {},
      url: `/api/orders/${orderId}/settle`,
    });
  }

  public async pay(gateway: PaymentGateway, id: string, targetUserId?: number): Promise<void> {
    await this.request({
      method: 'post',
      payload: {
        targetUserId,
        token: id,
      },
      url: `/api/pay/gateways/${gateway}`,
    });
  }

  public async resendOrderConfirmation(orderId: number): Promise<void> {
    await this.request({
      method: 'post',
      url: `/api/orders/${orderId}/email`,
      payload: {},
    });
  }

  public async getPaymentGateways(): Promise<PaymentGatewayConfig> {
    return await this.request<PaymentGatewayConfig>('/api/pay/gateways');
  }

  public async retrieveTermsOfAttendance(type: SettingTermKeys): Promise<Terms> {
    return await this.request<Terms>(`/api/policies/${type}`);
  }

  public async getConvention(id: number): Promise<ConventionSettingsInfo> {
    return await this.request<ConventionSettingsInfo>(`/api/conventions/${id}`);
  }

  public async getConventionList(): Promise<ConventionListItem[]> {
    return await this.request<ConventionListItem[]>(`/api/conventions`);
  }

  public async updateConvention(
    id: number,
    payload: Omit<Partial<ConventionSharedModel>, 'id'>,
  ): Promise<ConventionSharedModel> {
    return await this.request({
      method: 'patch',
      payload,
      url: `/api/conventions/${id}`,
    });
  }

  public async requestNukePII(id: number, payload: ConventionRedactOptions): Promise<void> {
    await this.request({
      method: 'post',
      payload,
      url: `/api/conventions/${id}/nuke`,
    });
  }

  public async cancelNukePII(id: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/conventions/${id}/nuke`,
    });
  }

  public async getTemplateList(): Promise<EmailTemplateList> {
    return await this.request<EmailTemplateList>(`/api/emails/overrides`);
  }

  public async getTemplate(name: string): Promise<EmailTemplateInfo> {
    return await this.request<EmailTemplateInfo>(`/api/emails/overrides/${name}`);
  }

  public async modifyOverride(
    name: string,
    body: string,
    subject: string | null,
    scope: 'convention' | 'organization',
    disabled?: boolean,
  ): Promise<void> {
    await this.request({
      method: 'post',
      payload: {
        body,
        disabled: disabled ?? false,
        scope,
        subject,
      },
      url: `/api/emails/overrides/${name}`,
    });
  }

  public async previewTemplate(name: string, body: string): Promise<EmailTemplatePreview> {
    return await this.request({
      method: 'post',
      payload: {
        name,
        body,
      },
      url: `/api/emails/overrides/render`,
    });
  }

  public async deleteOverride(name: string, scope: string): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/emails/overrides/${name}/${scope}`,
    });
  }

  public async getConfig(): Promise<Config> {
    const [config, userConfig] = await Promise.all([
      this.request<Config>('/api/config'),
      this.request<Partial<Config>>('/api/config/user'),
    ]);

    return deepMerge(config, userConfig) as Config;
  }

  public async getUserDealer(userId: number): Promise<DealerFullModel> {
    return await this.request<DealerFullModel>(`/api/users/${userId}/dealer`);
  }

  public async getUserPreviousDealer(userId: number): Promise<DealerFullModel> {
    return await this.request<DealerFullModel>(`/api/users/${userId}/dealer/previous`);
  }

  public async confirmAccount(code: string): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { code },
      url: '/api/registrations/confirm',
    });
  }

  public async confirmChangeEmail(code: string): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { code },
      url: '/api/registrations/email',
    });
  }

  public async pendingEmailChange(userId: number): Promise<{ pending: boolean }> {
    return await this.request(`/api/users/${userId}/email/pending`);
  }

  public async checkUserAttributeUnique(value: string, field: UniqueAttribute): Promise<boolean> {
    try {
      // If exists, 200. If not found, 404.
      await this.request(`/api/users/${field}/${encodeURIComponent(value)}`);
      return false;
    } catch (error) {
      if (!isResourceError(error)) {
        throw error;
      }

      return true;
    }
  }

  public async getVouchers(): Promise<VoucherListItem[]> {
    return await this.request('/api/vouchers');
  }

  public async getVoucher(voucherId: number): Promise<VoucherWithDetails> {
    return await this.request(`/api/vouchers/${voucherId}`);
  }

  public async createVoucher(payload: CreateVoucherModel): Promise<{ id: number }> {
    return await this.request<{ id: number }>({
      method: 'post',
      payload,
      url: `/api/vouchers`,
    });
  }

  public async updateVoucher(voucherId: number, payload: UpdateVoucherModel): Promise<void> {
    await this.request({
      method: 'patch',
      payload,
      url: `/api/vouchers/${voucherId}`,
    });
  }

  public async deleteVoucher(voucherId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/vouchers/${voucherId}`,
    });
  }

  public async getProductEligibility(
    userId: number,
    productId: number,
  ): Promise<ProductEligibility> {
    return await this.request(`/api/users/${userId}/eligibility/${productId}`);
  }

  public async addProductToCart(
    productId: number,
    options: {
      referenceId?: number;
      quantity?: number;
      options?: OptionDataPayload;
    },
  ): Promise<void> {
    await this.request({
      method: 'post',
      payload: options,
      url: `/api/products/${productId}/add`,
    });
  }

  public async removeProductFromCart(
    orderId: number,
    payload: ProductCartDeleteParams,
  ): Promise<void> {
    await this.request({
      method: 'delete',
      payload,
      url: `/api/orders/${orderId}/product`,
    });
  }

  public async deleteProductAddon(productId: number, addonId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/products/${productId}/addons/${addonId}`,
    });
  }

  public async createProductDiscount(
    productId: number,
    payload: CreateProductDiscountPayload,
  ): Promise<void> {
    await this.request({
      method: 'post',
      payload,
      url: `/api/products/${productId}/discounts`,
    });
  }

  public async updateProductDiscount(
    productId: number,
    discountId: number,
    payload: UpdateProductDiscountPayload,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      payload,
      url: `/api/products/${productId}/discounts/${discountId}`,
    });
  }

  public async deleteProductDiscount(productId: number, discountId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/products/${productId}/discounts/${discountId}`,
    });
  }

  public async deleteProductOption(productId: number, optionId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/products/${productId}/options/${optionId}`,
    });
  }

  public async updateProductOption(
    productId: number,
    optionId: number,
    required: boolean,
    mutable: boolean,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      url: `/api/products/${productId}/options/${optionId}`,
      payload: {
        required,
        mutable,
      },
    });
  }

  public async deleteProductUpgrade(productId: number, upgradeId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/products/${productId}/upgrades/${upgradeId}`,
    });
  }

  public async updateProductQuantity(
    orderId: number,
    orderItemId: number,
    quantity: number,
    force?: boolean,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { quantity, force },
      url: `/api/orders/${orderId}/item/${orderItemId}`,
    });
  }

  public async updateOrderItemOption(
    orderId: number,
    orderItemId: number,
    options: OptionDataPayload,
    attemptForce?: boolean,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      payload: {
        options,
        attemptForce,
      },
      url: `/api/orders/${orderId}/item/${orderItemId}/options`,
    });
  }

  public async removeStock(orderId: number, orderItemId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/orders/${orderId}/item/${orderItemId}/stock`,
    });
  }

  public async getOrdersByUserId(userId: number): Promise<Order[]> {
    return await this.request(`/api/users/${userId}/orders`);
  }

  public async getOptions(source: OptionSource): Promise<Option[]> {
    return await this.request(`/api/options/${source}`);
  }

  public async getOption(source: OptionSource, optionId: number): Promise<OptionInternal> {
    return await this.request(`/api/options/${source}/${optionId}`);
  }

  public async createOption(
    source: OptionSource,
    option: OptionSavePayload,
  ): Promise<OptionInternal> {
    return await this.request({
      method: 'post',
      payload: option,
      url: `/api/options/${source}`,
    });
  }

  public async saveOption(
    source: OptionSource,
    optionId: number,
    option: OptionSavePayload,
  ): Promise<Option> {
    return await this.request({
      method: 'patch',
      payload: option,
      url: `/api/options/${source}/${optionId}`,
    });
  }

  public async deleteOption(source: OptionSource, id: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/options/${source}/${id}`,
    });
  }

  public async getProducts(): Promise<ProductModel[]> {
    return await this.request('/api/products');
  }

  public async getProductById(id: number): Promise<ProductModel> {
    return await this.request(`/api/products/${id}`);
  }

  public async deleteProductById(id: number): Promise<ProductModel[]> {
    return await this.request({
      method: 'delete',
      url: `/api/products/${id}`,
    });
  }

  public async clearProductFromCarts(id: number): Promise<void> {
    await this.request({
      method: 'post',
      url: `/api/products/${id}/clear`,
    });
  }

  public async getPublicProductById(id: number): Promise<ProductModel> {
    return await this.request(`/api/products/${id}/public`);
  }

  public async getUserActiveRegistration(userId: number): Promise<RegistrationInfo> {
    return await this.request<RegistrationInfo>(`/api/users/${userId}/registration`);
  }

  public async getActiveRegistrations(): Promise<UserAllRegistrations> {
    return await this.request<UserAllRegistrations>(`/api/users/registrations`);
  }

  public async getRegistrationLimitReached(): Promise<{ limitReached: boolean }> {
    return await this.request<{ limitReached: boolean }>('/api/registrations/limit');
  }

  public async getPublicProducts(): Promise<ProductModel[]> {
    return await this.request<ProductModel[]>('/api/products/public');
  }

  public async getProductCategoryById(categoryId: number): Promise<ProductCategoryModel> {
    return await this.request<ProductCategoryModel>(`/api/products/categories/${categoryId}`);
  }

  public async getProductCategories(): Promise<ProductCategoryModel[]> {
    return await this.request<ProductCategoryModel[]>('/api/products/categories');
  }

  public async getActiveOrder(userId: number, create = false): Promise<Order> {
    return await this.request<Order>({
      query: {
        create,
      },
      url: `/api/orders/user/${userId}`,
    });
  }

  public async getOrderById(orderId: number): Promise<Order> {
    return await this.request<Order>(`/api/orders/${orderId}`);
  }

  public async getOrders(payload: OrderSearch): Promise<ListResponse<Order>> {
    return await this.request({
      method: 'post',
      payload,
      url: `/api/orders`,
    });
  }

  public async cancelOrder(orderId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/orders/${orderId}`,
    });
  }

  public async getOrdersForUser(userId: number): Promise<Order[]> {
    return await this.request(`/api/users/${userId}/orders`);
  }

  public async getTicketById(id: string): Promise<Ticket> {
    return await this.request<Ticket>(`/api/tickets/${id}`);
  }

  public async getSurcharges(): Promise<Surcharge[]> {
    return await this.request<Surcharge[]>('/api/surcharges');
  }

  public async deleteSurcharge(surchargeId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/surcharges/${surchargeId}`,
    });
  }

  public async getCategorySurcharges(categoryId: number): Promise<Surcharge[]> {
    return await this.request<Surcharge[]>(`/api/products/categories/${categoryId}/surcharges`);
  }

  public async deleteCategorySurcharge(categoryId: number, surchargeId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/products/categories/${categoryId}/surcharges/${surchargeId}`,
    });
  }

  public async getPlatformFees(): Promise<Surcharge[]> {
    return await this.request<Surcharge[]>('/api/surcharges/platform');
  }

  public async getSurchargeById(id: number): Promise<Surcharge> {
    return await this.request<Surcharge>(`/api/surcharges/${id}`);
  }

  public async getSurchargeProductsById(
    id: number,
    page: number,
  ): Promise<ListResponse<SurchargeProduct>> {
    return await this.base.listRequest<SurchargeProduct>(`/api/surcharges/${id}/products`, page);
  }

  public async getSurchargeOrderItemsById(
    id: number,
    page: number,
  ): Promise<ListResponse<SurchargeOrderItem>> {
    return await this.base.listRequest<SurchargeOrderItem>(
      `/api/surcharges/${id}/orderItems`,
      page,
    );
  }

  public async deleteSurchargeProduct(productId: number, surchargeId: number): Promise<void> {
    await this.base.request({
      method: 'delete',
      url: `/api/products/${productId}/surcharges/${surchargeId}`,
    });
  }

  public async getProductsByCategory(
    categoryId: number,
    currentProductId?: number,
  ): Promise<ProductModel[]> {
    return await this.request<ProductModel[]>({
      query: currentProductId ? { currentProductId } : undefined,
      url: `/api/products/categories/${categoryId}/public`,
    });
  }

  public async updateCategory(categoryId: number, eventStore: boolean): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { eventStore },
      url: `/api/products/categories/${categoryId}`,
    });
  }

  public async getFullProductsByCategory(
    categoryId: number,
    onlyActive = false,
  ): Promise<ProductModel[]> {
    return await this.request<ProductModel[]>({
      url: `/api/products/categories/${categoryId}/full`,
      query: { onlyActive },
    });
  }

  public async getDealerForm(): Promise<DealerForm> {
    return await this.request<DealerForm>('/api/dealers/form');
  }

  public async getBadgeArt(ignoreSettings = false): Promise<BadgeArt[]> {
    return await this.request<BadgeArt[]>({ url: '/api/badgeart', query: { ignoreSettings } });
  }

  public async deleteBadgeArt(id: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/badgeart/${id}`,
    });
  }

  public async getDepartments(): Promise<DepartmentWithLeads[]> {
    return await this.request<DepartmentWithLeads[]>('/api/departments');
  }

  public async getDealerAssistants(dealerId: number): Promise<DealerAssistanceModel[]> {
    return await this.request<DealerAssistanceModel[]>(`/api/dealers/${dealerId}/assistants`);
  }

  public async updateDealer(userId: number, payload: DealerModel): Promise<void> {
    await this.request({
      method: 'post',
      url: `/api/users/${userId}/dealer`,
      payload,
    });
  }

  public async deleteChildRegistration(childId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/children/${childId}`,
    });
  }

  public async getCurrentUserChildren(): Promise<ChildRegistration[]> {
    return await this.request<ChildRegistration[]>('/api/children');
  }

  public async getMenuLinks(): Promise<MenuLink[]> {
    return await this.request<MenuLink[]>('/api/menulinks');
  }

  public async getRaffleAddressLocation(
    addressCity: string,
    addressCountry: string,
    addressZipcode: string,
  ): Promise<UserLocation> {
    return await this.request<UserLocation>({
      query: { addressCity, addressCountry, addressZipcode },
      url: '/api/raffles/locations',
    });
  }

  /**
   * Get all fees
   */
  public async getAllFees(): Promise<FeeModel[]> {
    return await this.request<FeeModel[]>('/api/fees');
  }

  public async deleteFee(feeId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/fees/${feeId}`,
    });
  }

  public async completeReview(userId: number): Promise<void> {
    await this.request({
      method: 'post',
      url: `/api/users/${userId}/review/complete`,
      payload: {},
    });
  }

  public async getActiveVolunteers(): Promise<VolunteerModel[]> {
    return await this.request<VolunteerModel[]>('/api/volunteers');
  }

  public async getVolunteerStats(): Promise<VolunteerStats> {
    return await this.request('/api/volunteers/stats');
  }

  public async getUserVolunteerApplications(userId: number): Promise<VolunteerLoadModel> {
    return await this.request<VolunteerLoadModel>(`/api/users/${userId}/volunteers`);
  }

  public async getEmailTargets(): Promise<EmailTargets> {
    return await this.request<EmailTargets>('/api/emails/targets');
  }

  public async getEmailSenders(): Promise<Senders> {
    return await this.request<Senders>('/api/emails/senders');
  }

  public async getAttendeeEmails(payload: AttendeeEmails): Promise<{ userIds: number[] }> {
    return await this.request<{ userIds: number[] }>({
      method: 'post',
      payload,
      url: '/api/emails/list/attendees',
    });
  }

  public async sendMassEmail(payload: SendEmailPayload): Promise<MailQueueInfo[]> {
    return await this.request({
      method: 'post',
      payload,
      url: '/api/emails',
    });
  }

  public async deleteRegistration(id: number): Promise<void> {
    await this.request({ method: 'delete', url: `/api/registrations/${id}` });
  }

  public async transferRegistration(userId: number, targetUserId: number): Promise<void> {
    await this.request({
      method: 'post',
      payload: { targetUserId },
      url: `/api/registrations/${userId}/transfer/admin`,
    });
  }

  public async getRegistrationFlags(): Promise<RegistrationFlagModel[]> {
    return await this.request<RegistrationFlagModel[]>('/api/registrations/flags');
  }
  public async getRegistrationFlagUsers(flagId: number): Promise<RegistrationFlagUser[]> {
    return await this.request<RegistrationFlagUser[]>(`/api/registrations/flags/${flagId}/users`);
  }

  public async getRegistrationForm(userId?: number): Promise<ConRegistrationForm> {
    return await this.request<ConRegistrationForm>({
      query: userId === undefined ? undefined : { userId },
      url: '/api/registrations/form',
    });
  }

  public async getChildRegistration(registrationId: number): Promise<ChildRegistration[]> {
    return await this.request<ChildRegistration[]>(`/api/registrations/${registrationId}/children`);
  }

  public async getExtendedUser(userId: number): Promise<ExtendedUser> {
    return await this.request<ExtendedUser>(`/api/users/${userId}`);
  }

  public async getVolunteerConfig(): Promise<VolunteerConfig> {
    return await this.request<VolunteerConfig>('/api/volunteers/config');
  }

  public async updateVolunteerConfig(payload: Partial<VolunteerConfig>): Promise<void> {
    await this.request({
      method: 'patch',
      payload,
      url: '/api/volunteers/config',
    });
  }

  public async getDepartmentFromLead(): Promise<VolunteerModel[]> {
    return await this.request<VolunteerModel[]>('/api/volunteers/lead');
  }

  public async upsertVolunteer(userId: number, payload: VolunteerUpsertModel): Promise<void> {
    await this.request({ method: 'post', payload, url: `api/users/${userId}/volunteer` });
  }

  public async deleteVolunteer(volunteerId: number): Promise<void> {
    await this.request({ method: 'delete', url: `api/volunteers/${volunteerId}` });
  }

  public async getDealerSeatingPreferences(dealerId: number): Promise<SeatingPreference[]> {
    return await this.request<SeatingPreference[]>(`/api/dealers/${dealerId}/seatingPreferences`);
  }

  public async setDealerSeatingPreferenceStatus(
    dealerId: number,
    targetId: number,
    status: string,
  ): Promise<void> {
    await this.request({
      method: 'post',
      payload: { target: targetId, status },
      url: `/api/dealers/${dealerId}/seatingPreferences/update`,
    });
  }

  public async getUserVolunteer(userId: number): Promise<VolunteerModel> {
    return await this.request<VolunteerModel>(`/api/users/${userId}/volunteer`);
  }

  public async updateVolunteer(userId: number, payload: VolunteerUpsertModel): Promise<void> {
    await this.request({
      method: 'post',
      payload,
      url: `/api/users/${userId}/volunteer`,
    });
  }

  public async getVolunteerForm(): Promise<VolunteerForm> {
    return await this.request<VolunteerForm>('/api/volunteers/form');
  }

  public async getUserVolunteerStaff(volunteerId: number): Promise<StaffRegistrationInfo> {
    return await this.request(`api/volunteers/${volunteerId}/staff`);
  }

  public async makeVolunteerStaff(volunteerId: number): Promise<void> {
    await this.request({
      method: 'patch',
      payload: {},
      url: `/api/volunteers/${volunteerId}/makestaff`,
    });
  }

  public async removeVolunteerStaff(
    volunteerId: number,
    payload: RemoveStaffRequest,
  ): Promise<void> {
    await this.request({
      method: 'post',
      payload,
      url: `/api/volunteers/${volunteerId}/removestaff`,
    });
  }

  public async createDepartmentRequest(
    volunteerId: number,
    departmentId: number,
    staffRequest?: boolean,
  ): Promise<void> {
    await this.request({
      method: 'put',
      payload: { departmentId, staffRequest },
      url: `/api/volunteers/${volunteerId}/assignment/request`,
    });
  }

  public async deleteDepartmentRequest(volunteerId: number, departmentId: number): Promise<void> {
    await this.request({
      method: 'delete',
      payload: { departmentId },
      url: `/api/volunteers/${volunteerId}/assignment/request`,
    });
  }

  public async clearDepartmentRequests(volunteerId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `api/volunteers/${volunteerId}/assignment/clear`,
    });
  }

  public async addAssignmentToDepartment(volunteerId: number, departmentId: number): Promise<void> {
    await this.request({
      method: 'post',
      payload: { departmentId },
      url: `/api/volunteers/${volunteerId}/assignment`,
    });
  }

  public async removeAssignmentFromDepartment(
    volunteerId: number,
    departmentId: number,
  ): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/volunteers/${volunteerId}/assignment/${departmentId}`,
    });
  }

  public async getDealerAreas(): Promise<DealerArea[]> {
    return await this.request({ url: '/api/dealers/areas' });
  }

  public async createDealerArea(payload: {
    name: string;
    description: string;
    enabled: boolean;
    full: boolean;
    open: boolean;
    categoryId: number;
  }): Promise<void> {
    await this.request({
      url: `/api/areas`,
      method: 'post',
      payload,
    });
  }

  public async updateDealerArea(
    areaId: number,
    payload: {
      name: string;
      description: string;
      enabled: boolean;
      full: boolean;
      open: boolean;
      categoryId: number;
    },
  ): Promise<void> {
    await this.request({
      url: `/api/areas/${areaId}`,
      method: 'patch',
      payload,
    });
  }

  public async deleteDealerArea(areaId: number): Promise<void> {
    await this.request({
      url: `/api/areas/${areaId}`,
      method: 'delete',
    });
  }

  public async getAllDealers(areaIds: number[]): Promise<DealerFullModel[]> {
    return await this.request({
      method: 'post',
      payload: {
        areaIds,
      },
      url: '/api/dealers/search',
    });
  }

  public async getDealer(dealerId: number): Promise<DealerFullModel> {
    return await this.request(`/api/dealers/${dealerId}`);
  }

  public async getDealerConfig(): Promise<DealerConfig> {
    return await this.request(`/api/dealers/config`);
  }

  public async updateDealerConfig(config: Partial<DealerConfig>): Promise<void> {
    await this.request({
      method: 'patch',
      payload: config,
      url: `/api/dealers/config`,
    });
  }

  public async getNotes(search: UserNoteSearch): Promise<UserNoteSummary[]> {
    return await this.request({
      method: 'post',
      payload: search,
      url: `/api/notes/search`,
    });
  }

  public async createUserNote(payload: UserNoteCreate): Promise<void> {
    await this.request({
      method: 'post',
      payload,
      url: `/api/notes`,
    });
  }

  public async deleteUserNote(noteId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/notes/${noteId}`,
    });
  }

  public async deleteActiveDealerApplicationPayment(
    dealerId: number,
    areaId: number,
  ): Promise<void> {
    await this.request({ method: 'delete', url: `api/dealers/${dealerId}/area/${areaId}/payment` });
  }

  public async deleteActiveDealerApplication(dealerId: number, areaId: number): Promise<void> {
    await this.request({ method: 'delete', url: `api/dealers/${dealerId}/area/${areaId}` });
  }

  public async updateDealerApplication(
    dealerId: number,
    areaId: number,
    payload: UpdateDealerApplicationPayload,
  ): Promise<void> {
    await this.request({ method: 'patch', payload, url: `api/dealers/${dealerId}/area/${areaId}` });
  }

  public async getPolicies(): Promise<Policy[]> {
    return await this.request<Policy[]>('/api/policies');
  }

  public async upsertPolicy(payload: PolicyCreate): Promise<void> {
    await this.request({
      method: 'put',
      payload,
      url: '/api/policies',
    });
  }

  public async deletePolicy(payload: PolicyDelete): Promise<void> {
    await this.request({
      method: 'delete',
      payload,
      url: `/api/policies`,
    });
  }

  public async updateUserRole(
    userId: number,
    roleId: number,
    action: 'add' | 'remove',
    scope: RoleScope,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { roleId, action, scope },
      url: `/api/users/${userId}/roles`,
    });
  }

  public async getUserRoles(): Promise<RoleWithPermissions[]> {
    return await this.request<RoleWithPermissions[]>('/api/users/roles');
  }

  public async getRoles(): Promise<Role[]> {
    return await this.request<Role[]>('/api/roles');
  }

  public async getInvite(code: string): Promise<RoleInviteResponse> {
    return await this.request(`/api/roles/invites/${code}`);
  }

  public async acceptInvite(roleId: number, inviteId: number, code: string): Promise<void> {
    await this.request({
      method: 'put',
      payload: {
        code,
      },
      url: `/api/roles/${roleId}/invites/${inviteId}/accept`,
    });
  }

  public async getRoleInvites(roleId: number): Promise<RoleInvite[]> {
    return await this.request<RoleInvite[]>(`/api/roles/${roleId}/invites`);
  }

  public async getRoleInvite(roleId: number, inviteId: number): Promise<RoleInvite> {
    return await this.request<RoleInvite>(`/api/roles/${roleId}/invites/${inviteId}`);
  }

  public async createRoleInvites(roleId: number, payload: CreateRoleInvitesPayload): Promise<void> {
    await this.request({
      method: 'post',
      payload,
      url: `/api/roles/${roleId}/invites`,
    });
  }

  public async updateRoleInvite(
    roleId: number,
    inviteId: number,
    payload: UpdateRoleInvitesPayload,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      payload,
      url: `/api/roles/${roleId}/invites/${inviteId}`,
    });
  }

  public async updatePermissionRole(
    roleId: number,
    permissionId: number,
    invert: boolean,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      payload: {
        invert,
      },
      url: `/api/roles/${roleId}/permissions/${permissionId}`,
    });
  }

  public async deletePermissionRole(roleId: number, permissionId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/roles/${roleId}/permissions/${permissionId}`,
    });
  }

  public async deleteRole(roleId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/roles/${roleId}`,
    });
  }

  public async getRoleUsers(roleId: number): Promise<RoleUserListItem[]> {
    return await this.request<RoleUserListItem[]>(`/api/roles/${roleId}/users`);
  }

  public async getPermissions(): Promise<Permission[]> {
    return await this.request<Permission[]>('/api/permissions');
  }

  public async getUserPermissions(userId: number): Promise<UserPermissionVerbose[]> {
    return await this.request<UserPermissionVerbose[]>(`/api/users/${userId}/permissions`);
  }

  public async deleteUserPermission(userId: number, permissionId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/users/${userId}/permissions/${permissionId}`,
    });
  }

  public async updateUserPermission(
    userId: number,
    permissionId: number,
    invert: boolean,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      payload: {
        invert,
      },
      url: `/api/users/${userId}/permissions/${permissionId}`,
    });
  }

  public async getUserCashierProfile(userId: number, barcode?: string): Promise<CashierCheck> {
    return await this.request<CashierCheck>({
      query: barcode
        ? {
            barcode,
          }
        : {},
      url: `/api/users/${userId}/cashier`,
    });
  }

  public async markRegistrationBadgeAsPrinted(
    userId: number,
    checkin: boolean | null,
    barcode?: string,
    reason?: string,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      payload: {
        barcode,
        checkin,
        reason,
      },
      url: `/api/users/${userId}/print`,
    });
  }

  public async generateNfcBadge(userId: number, uuid: string): Promise<NFCBadge> {
    return await this.request<NFCBadge>({
      method: 'post',
      payload: {
        userId,
        uuid,
      },
      url: `/api/badge/nfc/program`,
    });
  }

  public async getNfcPassword(uuid: string): Promise<NFCPassword> {
    return await this.request<NFCPassword>({
      method: 'get',
      query: {
        uuid,
      },
      url: `/api/badge/nfc/password`,
    });
  }

  public async deleteNfcBadge(uuid: string): Promise<void> {
    await this.request({
      method: 'delete',
      query: { uuid },
      url: `/api/badge/nfc`,
    });
  }

  public async getNfcPublicKey(): Promise<JsonWebKey> {
    return await this.request<JsonWebKey>('/api/nfc/key');
  }

  public async getRefunds(): Promise<RefundResponse> {
    return await this.request<RefundResponse>('/api/refunds');
  }

  public async getAttendeeStats(): Promise<AttendeeStats> {
    return await this.request<AttendeeStats>(`/api/stats/attendeeStats`);
  }

  public async getIdentityStats(): Promise<IdentityStats> {
    return await this.request<IdentityStats>(`/api/stats/identityStats`);
  }

  public async confirmAssistant(userId: number, code: string): Promise<void> {
    await this.request({
      method: 'patch',
      payload: {
        code,
      },
      url: `/api/users/${userId}/assistant`,
    });
  }

  public async getRegistrationsOverTime(): Promise<RegsOverTime> {
    return await this.request<RegsOverTime>(`/api/stats/registrationsOverTime`);
  }

  public async getRegistrationsOverYear(): Promise<RegsOverYear> {
    return await this.request<RegsOverYear>(`/api/stats/registrationsOverYear`);
  }

  public async getRegistrationsProductStats(): Promise<RegistrationStats> {
    return await this.request<RegistrationStats>(`/api/stats/registrationStats`);
  }

  public async getSalesStats(): Promise<SalesReport> {
    return await this.request<SalesReport>(`/api/stats/sales`);
  }

  public async getDealerStats(): Promise<DealerStats> {
    return await this.request<DealerStats>(`/api/dealers/stats`);
  }

  public async getOptionsStats(): Promise<OptionsStats> {
    return await this.request<OptionsStats>(`/api/stats/options`);
  }

  public async getOptionStats(optionId: number): Promise<OptionStatsData> {
    return await this.request<OptionStatsData>(`/api/stats/options/${optionId}`);
  }

  public async getCashierStats(): Promise<CashierStats[]> {
    return await this.request<CashierStats[]>(`/api/stats/cashier`);
  }

  public async getCashierStatsByDay(date: string): Promise<CashierStatsByDay> {
    return await this.request<CashierStatsByDay>(`/api/stats/cashier/prints?date=${date}`);
  }

  public async getOAuthApplicationAuthorizationInfo(
    clientId: string,
    scope: string,
    redirectUri: string,
  ): Promise<OAuthApp> {
    return await this.request<OAuthApp>({
      query: {
        redirect_uri: redirectUri,
        scope,
      },
      url: `/api/oauth/${clientId}`,
    });
  }

  public async authorizeOAuthApplication(
    clientId: number,
    redirectUri: string,
    responseType: string,
    scope?: string,
  ): Promise<OAuthAuthorization> {
    return await this.request<OAuthAuthorization>({
      method: 'post',
      payload: {
        redirect_uri: redirectUri,
        response_type: responseType,
        scope,
      },
      url: `/api/oauth/${clientId}/authorize`,
    });
  }

  public async checkEmailJobStatus(id: string): Promise<EmailQueueStatus> {
    return await this.request<EmailQueueStatus>({
      query: { id },
      url: '/api/emails/status',
    });
  }

  public async previewEmail(input: string): Promise<EmailPreview> {
    return await this.request<EmailPreview>({
      method: 'post',
      payload: {
        body: input,
      },
      url: '/api/emails/preview',
    });
  }

  public async requestVerificationEmail(): Promise<void> {
    await this.request({
      method: 'post',
      url: `/api/users/verify`,
      payload: {},
    });
  }

  public async estimateEmailRecipients(recipients: string): Promise<EmailEstimate> {
    return await this.request<EmailEstimate>({
      query: {
        recipients,
      },
      url: '/api/emails/estimate',
    });
  }

  public async createAsset(category: string, file: Blob): Promise<AssetCreateResult> {
    return await this.base.sendFile<AssetCreateResult>({
      method: 'post',
      payload: this.fixupImageBlob(file),
      progressor: () => undefined,
      url: `/api/assets/${category}`,
    });
  }

  public async createBadgeArt(assetId: string): Promise<void> {
    await this.request({
      method: 'post',
      payload: {
        assetId,
      },
      url: '/api/badgeart',
    });
  }

  /* Badge Designer Endpoints */
  public async createBadge(name: string, content: DesignerNode[]): Promise<{ id: number }> {
    return await this.request({
      method: 'post',
      payload: { content, name },
      url: '/api/badge/designs',
    });
  }

  public async updateBadge(id: number, name: string, content: DesignerNode[]): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { content, name },
      url: `/api/badge/designs/${id}`,
    });
  }

  public async getBadgePrintsForUser(userId: number): Promise<UserBadgePrintStats[]> {
    return await this.request(`/api/badge/prints/${userId}`);
  }

  public async getBadgeDesignList(): Promise<BadgeDesign[]> {
    return await this.request('/api/badge/designs');
  }

  public async getBadgeDesign(id: number | 'default'): Promise<BadgeDesign> {
    return await this.request(`/api/badge/designs/${id}`);
  }

  public async deleteBadge(id: number): Promise<void> {
    await this.request({ method: 'delete', url: `/api/badge/designs/${id}` });
  }

  public async setDefaultBadgeDesign(id: number): Promise<BadgeDesign> {
    return await this.request({
      method: 'post',
      payload: { badgeDesignId: id },
      url: '/api/badge/designs/default',
    });
  }

  public async getBadgeData(payload: BadgeDataRequest): Promise<BadgeData[]> {
    return await this.request({
      method: 'post',
      payload,
      url: '/api/badge/data',
    });
  }

  public async setLogo(file: File): Promise<AssetCreateResult> {
    return await this.base.sendFile<AssetCreateResult>({
      method: 'post',
      payload: this.fixupImageBlob(file),
      progressor: () => undefined,
      url: '/api/logo',
    });
  }

  public async createProductImage(productId: number, assetId: string): Promise<void> {
    await this.request({
      method: 'post',
      payload: {
        assetId,
      },
      url: `/api/products/${productId}/images`,
    });
  }

  public async updateProductImageType(
    productId: number,
    imageId: string,
    type: string,
  ): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { type },
      url: `/api/products/${productId}/images/${imageId}`,
    });
  }

  public async deleteProductImage(productId: number, imageId: string): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/products/${productId}/images/${imageId}`,
    });
  }

  public async getAuditLogs(payload: {
    entityId: string;
    objectId?: string;
    time?: number;
    reverse?: boolean;
    offset?: number;
    type?: string;
    pageSize: number;
    include: string[];
    exclude: string[];
  }): Promise<LogResult> {
    return await this.request({
      method: 'post',
      payload,
      url: '/api/auditlogs',
    });
  }

  public async getAuditEntities(): Promise<{ name: string }[]> {
    return await this.request('/api/auditlogs/entities');
  }

  public async getRaffleById(id: number): Promise<RaffleWithUserDetails> {
    return await this.request(`/api/raffles/${id}`);
  }

  public async deleteRaffle(id: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/raffles/${id}`,
    });
  }

  public async getRaffleTicketsById(
    id: number,
    page: number,
    search: string,
    status: string,
  ): Promise<ListResponse<RaffleTicket>> {
    return await this.base.listRequest<RaffleTicket>({
      query: { search, page, status },
      url: `/api/raffles/${id}/tickets`,
    });
  }

  public async getFullRaffleById(id: number): Promise<Raffle> {
    return await this.request(`/api/raffles/${id}/full`);
  }

  public async getRaffles(): Promise<Raffle[]> {
    return await this.request('/api/raffles');
  }

  public async getSocialLink(name: string): Promise<SocialLinkEndpoint> {
    return await this.request(`/api/sociallink/provider/${name}`);
  }

  public async getSocialLinkUser(userId: number, name: string): Promise<SocialLink> {
    return await this.request(`/api/user/${userId}/sociallink/${name}`);
  }

  public async loginSocialLink(code: string): Promise<{ id: string; name: string }> {
    return await this.request({
      method: 'post',
      payload: { code },
      url: '/api/sociallink',
    });
  }

  public async deleteSocialLink(id: string): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/sociallink/${id}`,
    });
  }

  public async setRaffleTicket(
    raffleId: number,
    userId: number,
    status: string,
    points: number,
  ): Promise<void> {
    await this.request({
      method: 'post',
      payload: { status, userId, points },
      url: `/api/raffles/${raffleId}/ticket`,
    });
  }

  public async enterRaffle(raffleId: number): Promise<void> {
    await this.request({
      method: 'post',
      payload: { join: true },
      url: `/api/raffles/${raffleId}/participate`,
    });
  }

  public async leaveRaffle(raffleId: number): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/raffles/${raffleId}/participate`,
    });
  }

  public async getRedirectRequest(conventionId: number): Promise<{ token: string | null }> {
    return await this.request<{ token: string | null }>({
      method: 'post',
      payload: { conventionId },
      url: '/api/login/redirect_token',
    });
  }

  public async getStripeVerifyKey(): Promise<{
    publishableKey: string;
    remainingIdentityVerifications: number;
  }> {
    return await this.request<{ publishableKey: string; remainingIdentityVerifications: number }>({
      method: 'get',
      url: '/api/identity/key',
    });
  }

  public async fetchStripeVerifyToken(): Promise<NewIdentitySession> {
    return await this.request<NewIdentitySession>({
      method: 'post',
      url: '/api/identity/session',
    });
  }

  public async getKioskList(): Promise<{ id: string }> {
    return await this.request<{ id: string }>({
      method: 'get',
      url: '/api/kiosk',
    });
  }

  public async updateRegistrationFormLayout(registrationForm: FormPageModel): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { registrationForm },
      url: '/api/convention/registration/form',
    });
  }

  public async getRegistrationFormLayout(force?: boolean): Promise<FormPageModel> {
    return await this.request<FormPageModel>({
      method: 'get',
      query: { force: force ? 'true' : 'false' },
      url: '/api/convention/registration/form',
    });
  }

  public async updateDealerFormLayout(dealerForm: FormPageModel): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { dealerForm },
      url: '/api/convention/dealer/form',
    });
  }

  public async getDealerFormLayout(): Promise<FormPageModel> {
    return await this.request<FormPageModel>({
      method: 'get',
      url: '/api/convention/dealer/form',
    });
  }

  public async updateVolunteerFormLayout(volunteerForm: FormPageModel): Promise<void> {
    await this.request({
      method: 'patch',
      payload: { volunteerForm },
      url: '/api/convention/volunteer/form',
    });
  }

  public async getVolunteerFormLayout(): Promise<FormPageModel> {
    return await this.request<FormPageModel>({
      method: 'get',
      url: '/api/convention/volunteer/form',
    });
  }

  public async fetchStripeIdentityInfo(id: string): Promise<StripeIdentityUser> {
    return await this.request<StripeIdentityUser>({
      method: 'get',
      url: `/api/identity/session/${id}`,
    });
  }

  public async exportSingleUser(userId: number, excludeAuditLogs?: boolean): Promise<object> {
    return await this.request<object>({
      method: 'get',
      url: `/api/users/${userId}/export`,
      query: excludeAuditLogs ? { excludeAuditLogs } : undefined,
    });
  }

  public async pendingExportRequests(): Promise<ExportListItem[]> {
    return await this.request<ExportListItem[]>({
      method: 'get',
      url: '/api/exports/pending',
    });
  }

  public async requestExportPiiAccess(): Promise<ExportRequest> {
    return await this.request<ExportRequest>({
      method: 'post',
      url: '/api/export/request',
    });
  }

  public async approveExportRequest(id: string): Promise<void> {
    await this.request({
      method: 'post',
      url: `/api/exports/${id}/approve`,
    });
  }

  public async rejectExportRequest(id: string): Promise<void> {
    await this.request({
      method: 'post',
      url: `/api/exports/${id}/reject`,
    });
  }

  public async deleteExportRequest(id: string): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/exports/${id}`,
    });
  }

  public async getOAuthApplications(): Promise<OAuthApplicationModel[]> {
    return await this.request<OAuthApplicationModel[]>({
      method: 'get',
      url: `/api/oauth`,
    });
  }

  public async createOAuthApplication(
    app: Omit<OAuthApplicationModel, 'id'>,
  ): Promise<NewSecretModel> {
    return await this.request<NewSecretModel>({
      method: 'post',
      payload: app,
      url: `/api/oauth`,
    });
  }

  public async updateOAuthApplication(
    id: number,
    app: Omit<OAuthApplicationModel, 'id'>,
  ): Promise<NewSecretModel> {
    return await this.request<NewSecretModel>({
      method: 'patch',
      payload: app,
      url: `/api/oauth/${id}`,
    });
  }

  public async deleteOAuthApplication(id: number): Promise<NewSecretModel> {
    return await this.request<NewSecretModel>({
      method: 'delete',
      url: `/api/oauth/${id}`,
    });
  }

  public async getOAuthPermissions(): Promise<PermissionName[]> {
    return await this.request<PermissionName[]>({
      method: 'get',
      url: `/api/oauth/permissions`,
    });
  }

  public async authorizeDevice(
    type: string,
    userId?: number,
  ): Promise<{ jwt: string; expires: Date }> {
    const query: { type: string; userId?: number } = { type };

    if (userId) {
      query.userId = userId;
    }

    return await this.request({
      method: 'get',
      url: `/api/devices/authorize`,
      query,
    });
  }

  public async getUserDeviceList(userId: number): Promise<Device[]> {
    return await this.request<Device[]>({
      method: 'get',
      url: `/api/users/${userId}/devices`,
    });
  }

  public async getPaymentGatewayConfigs(): Promise<PaymentGatewayConfiguration> {
    return await this.request<PaymentGatewayConfiguration>({
      method: 'get',
      url: `/api/pay/config`,
    });
  }

  public async createStripeConnectLink(
    organizationWide: boolean,
  ): Promise<StripeConnectLinkResponse> {
    return await this.request<StripeConnectLinkResponse>({
      method: 'post',
      payload: {
        organizationWide,
      },
      url: `/api/pay/config/stripe/connect`,
    });
  }

  public async setPagSeguroCheckoutConfig(data: PagSeguroConfiguration): Promise<void> {
    await this.request({
      method: 'post',
      url: `/api/pay/config/pagsegurocheckout`,
      payload: data,
    });
  }

  public async unsetPagSeguroCheckoutConfig(): Promise<void> {
    await this.request({
      method: 'delete',
      url: `/api/pay/config/pagsegurocheckout`,
    });
  }

  public async createPagSeguroCheckoutLink(): Promise<{ payLink?: string }> {
    return await this.request<{ payLink?: string }>({
      method: 'post',
      url: '/api/pay/checkouts/pagsegurocheckout/create',
    });
  }

  public async getOrderPagSeguroCheckouts(id: number): Promise<GetOrderPagSeguroCheckoutsResponse> {
    return await this.request<GetOrderPagSeguroCheckoutsResponse>({
      method: 'get',
      url: `/api/pay/checkouts/order/${id}/pagsegurocheckout`,
    });
  }

  public async deactivateOrderPagSeguroCheckout(
    orderId: number,
    checkoutId: number,
  ): Promise<object> {
    return await this.request({
      method: 'post',
      url: `/api/pay/checkouts/order/${orderId}/pagsegurocheckout/${checkoutId}/deactivate`,
    });
  }

  public async payWithCash(token: string, targetUserId: number): Promise<void> {
    await this.request({
      method: 'post',
      payload: { token, targetUserId },
      url: `/api/pay/gateways/cash`,
    });
  }

  public async payWithCard(token: string, targetUserId: number): Promise<void> {
    await this.request({
      method: 'post',
      payload: { token, targetUserId },
      url: `/api/pay/gateways/card`,
    });
  }

  public async getHotels(): Promise<FullHotel[]> {
    return await this.request<FullHotel[]>({
      method: 'get',
      url: `/api/hotel/hotels`,
    });
  }

  public async getPublicHotelReleases(): Promise<HotelPublicInformation> {
    return await this.request<HotelPublicInformation>({
      method: 'get',
      url: `/api/hotel/releases/public`,
    });
  }

  public async getAllHotelReleases(): Promise<ExtendedHotelReleaseGroup[]> {
    return await this.request<ExtendedHotelReleaseGroup[]>({
      method: 'get',
      url: `/api/hotel/releases/all`,
    });
  }

  public async getPublicHotelReleaseById(id: string): Promise<PublicHotelReleaseGroup> {
    return await this.request<PublicHotelReleaseGroup>({
      method: 'get',
      url: `/api/hotel/releases/${id}/public`,
    });
  }

  public async getHotelReleaseById(id: string): Promise<ExtendedHotelReleaseGroup> {
    return await this.request<ExtendedHotelReleaseGroup>({
      method: 'get',
      url: `/api/hotel/releases/${id}`,
    });
  }

  public async updateHotelReleaseById(
    id: string,
    payload: HotelReleaseGroupUpdateAttributes,
  ): Promise<HotelReleaseGroup> {
    return await this.request<HotelReleaseGroup>({
      method: 'put',
      payload,
      url: `/api/hotel/releases/${id}`,
    });
  }

  public async getHotelReleaseGroupEntries(id: string): Promise<HotelReleaseGroupEntryList> {
    return await this.request<HotelReleaseGroupEntryList>({
      method: 'get',
      url: `/api/hotel/releases/${id}/entries`,
    });
  }

  public async getAllHotelReservations(): Promise<HotelReservation[]> {
    return await this.request<HotelReservation[]>({
      method: 'get',
      url: `/api/hotel/reservations/all`,
    });
  }

  public async getHotelReservationById(id: string): Promise<HotelReservation> {
    return await this.request<HotelReservation>({
      method: 'get',
      url: `/api/hotel/reservations/${id}`,
    });
  }

  public async getHotelUserSummary(id: string): Promise<UserHotelSummary> {
    return await this.request<UserHotelSummary>({
      method: 'get',
      url: `/api/user/${id}/hotel`,
    });
  }

  public async linkHotelReservation(id: string): Promise<HotelReservation> {
    return await this.request<HotelReservation>({
      method: 'patch',
      payload: { id },
      url: '/api/hotel/reservations/link',
    });
  }

  public async createHotelReleaseGroup(payload: HotelReleaseGroupCreateAttributes): Promise<void> {
    await this.request<HotelReleaseGroup>({
      method: 'post',
      payload,
      url: `/api/hotel/releases`,
    });
  }

  public async getHotelIntegrations(): Promise<HotelIntegration[]> {
    return await this.request<HotelIntegration[]>({
      method: 'get',
      url: `/api/hotel/integrations`,
    });
  }

  public async getReleaseEntry(releaseId: string): Promise<HotelReleaseGroupEntries> {
    return await this.request<HotelReleaseGroupEntries>({
      method: 'get',
      url: `/api/hotel/releases/${releaseId}/entry`,
    });
  }

  public async createReleaseEntry(
    releaseId: string,
    reservationTermsAgreed: boolean,
  ): Promise<HotelReleaseGroupEntries> {
    return await this.request<HotelReleaseGroupEntries>({
      method: 'post',
      url: `/api/hotel/releases/${releaseId}/entry`,
      payload: { reservationTermsAgreed },
    });
  }

  public async deleteReleaseEntry(entryId: string, force = false): Promise<void> {
    await this.request({
      method: 'delete',
      query: { force },
      url: `/api/hotel/entries/${entryId}`,
    });
  }

  public async getHotelStats(): Promise<HotelStats> {
    return await this.request<HotelStats>({
      method: 'get',
      url: `/api/hotel/stats`,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
  public async request<T extends object | void = void>(
    opts: RequestOptions<object | undefined> | string,
  ): Promise<T> {
    try {
      return await this.base.request<T>(opts);
    } catch (error) {
      const url = opts instanceof Object ? opts.url : opts;
      const noRedirect = url === '/api/users/current' || url === '/api/users/logout';

      // If we're not authenticated, redirect to the login page
      if (isAuthError(error as Error, AuthError.NotAuthenticated) && !noRedirect) {
        window.location.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
      }

      throw error;
    }
  }

  /**
   * Required until https://github.com/puppeteer/puppeteer/commit/532ae573d29063ffd081ed9ee340d71b25320dec is
   * in puppeteers latest release.
   * See https://github.com/puppeteer/puppeteer/pull/5358 for explanation.
   */
  private fixupImageBlob(blob: Blob): Blob {
    if (blob.type === '') {
      return blob.slice(0, blob.size, 'image/png');
    }

    return blob;
  }
}
